// client/src/pages/POA/poaConstants.ts
// IRS-sourced picklist values for Form 2848.

export const MATTER_DESCRIPTIONS = [
  'Income',
  'Income (pass-through items)',
  'Employment',
  'Payroll',
  'Excise',
  'Estate',
  'Gift',
  'Whistleblower',
  'Practitioner Discipline',
  'Private Letter Ruling (PLR)',
  'FOIA Request',
  'Civil Penalty',
  'Sec. 4980H Shared Responsibility Payment',
  'Innocent Spouse Relief',
  'Centralized Partnership Audit (BBA)',
  'IRA Civil Penalty',
  'Other (specify)',
] as const;

// Common tax form numbers — searchable dropdown + free-text fallback
export const COMMON_TAX_FORMS: { value: string; label: string }[] = [
  { value: '1040',    label: '1040 — Individual Income Tax' },
  { value: '1040-SR', label: '1040-SR — US Tax Return for Seniors' },
  { value: '1040-NR', label: '1040-NR — US Nonresident Alien Income Tax' },
  { value: '1041',    label: '1041 — Income Tax for Estates and Trusts' },
  { value: '1065',    label: '1065 — US Return of Partnership Income' },
  { value: '1120',    label: '1120 — US Corporation Income Tax Return' },
  { value: '1120-S',  label: '1120-S — S Corporation Income Tax Return' },
  { value: '706',     label: '706 — Estate Tax Return' },
  { value: '709',     label: '709 — Gift Tax Return' },
  { value: '720',     label: '720 — Quarterly Federal Excise Tax' },
  { value: '940',     label: '940 — Annual FUTA Tax Return' },
  { value: '941',     label: '941 — Quarterly Employment Tax' },
  { value: '944',     label: '944 — Annual Federal Tax Return (small employer)' },
  { value: '945',     label: '945 — Annual Return of Withheld Federal Income Tax' },
  { value: '990',     label: '990 — Return of Organization Exempt From Income Tax' },
  { value: '8849',    label: '8849 — Claim for Refund of Excise Taxes' },
  { value: '8857',    label: '8857 — Request for Innocent Spouse Relief' },
  { value: 'CT-1',    label: 'CT-1 — Railroad Retirement Tax Return' },
  // Civil penalty and other non-form matters are listed on Line 3 with no form number.
  { value: 'Not Applicable', label: 'Not Applicable — civil penalty / non-form matter' },
];

// Part II designation codes — ordered by most common for a CPA firm
export const DESIGNATION_CODES: { value: string; label: string; requiresLicense: boolean }[] = [
  { value: 'b', label: 'b — Certified Public Accountant (CPA)',    requiresLicense: true  },
  { value: 'a', label: 'a — Attorney',                             requiresLicense: true  },
  { value: 'c', label: 'c — Enrolled Agent',                       requiresLicense: true  },
  { value: 'd', label: 'd — Officer of Taxpayer Organization',     requiresLicense: false },
  { value: 'e', label: 'e — Full-Time Employee of Taxpayer',       requiresLicense: false },
  { value: 'f', label: 'f — Family Member',                        requiresLicense: false },
  { value: 'g', label: 'g — Enrolled Actuary',                     requiresLicense: true  },
  { value: 'h', label: 'h — Unenrolled Return Preparer',           requiresLicense: false },
  { value: 'k', label: 'k — Qualifying Student or Law Graduate',   requiresLicense: true  },
  { value: 'r', label: 'r — Enrolled Retirement Plan Agent',       requiresLicense: true  },
];

// ── Licensing jurisdiction (Part II) ────────────────────────────────────────
// Form 2848 asks for "Licensing jurisdiction (State) or other licensing authority".
// Not every designation is licensed by a state: enrolled agents and ERPAs are
// enrolled by the IRS itself, and enrolled actuaries by the Joint Board. Offering
// only states forced those reps to record a jurisdiction that isn't true.
export const NON_STATE_JURISDICTIONS = [
  'IRS',
  'Joint Board for the Enrollment of Actuaries',
];

export const US_STATES = [
  'Alabama','Alaska','Arizona','Arkansas','California','Colorado','Connecticut',
  'Delaware','Florida','Georgia','Hawaii','Idaho','Illinois','Indiana','Iowa',
  'Kansas','Kentucky','Louisiana','Maine','Maryland','Massachusetts','Michigan',
  'Minnesota','Mississippi','Missouri','Montana','Nebraska','Nevada','New Hampshire',
  'New Jersey','New Mexico','New York','North Carolina','North Dakota','Ohio',
  'Oklahoma','Oregon','Pennsylvania','Rhode Island','South Carolina','South Dakota',
  'Tennessee','Texas','Utah','Vermont','Virginia','Washington','West Virginia',
  'Wisconsin','Wyoming','District of Columbia',
  // Territories — a CPA licensed in PR or the USVI practices before the IRS the same way.
  'Puerto Rico','Guam','U.S. Virgin Islands','American Samoa','Northern Mariana Islands',
];

/** Every accepted jurisdiction value, non-state authorities first. */
export const LICENSING_JURISDICTIONS = [...NON_STATE_JURISDICTIONS, ...US_STATES];

/**
 * Designations whose licensing authority is fixed rather than a state. Picking one of
 * these snaps the jurisdiction to the right authority instead of leaving a state
 * sitting in the field — an enrolled agent is enrolled by the IRS, not by Alabama.
 */
export const DESIGNATION_FIXED_JURISDICTION: Record<string, string> = {
  c: 'IRS',                                          // Enrolled Agent
  r: 'IRS',                                          // Enrolled Retirement Plan Agent
  g: 'Joint Board for the Enrollment of Actuaries',   // Enrolled Actuary
};

// ── Civil penalty coverage ──────────────────────────────────────────────────
// Standing recommendation from our IRS-side representative: carry Civil Penalty
// coverage on every POA, so a penalty question surfacing mid-call or mid-exam
// doesn't mean going back to the client for a fresh 2848.
//
// Set this to false to stop pre-adding the row to new POAs. The manual
// "Add Civil Penalty coverage" button on the Tax Matters step stays available either
// way, so turning this off costs one click per POA rather than losing the feature.
export const DEFAULT_CIVIL_PENALTY_COVERAGE = true;

export const CIVIL_PENALTY_DESCRIPTION = 'Civil Penalty';
export const CIVIL_PENALTY_FORM_NUMBER = 'Not Applicable';

// ── Client reminders ────────────────────────────────────────────────────────
// The reminder email carries a direct signing link only when we captured one from
// SafeSend's send-for-signature response, and we only started capturing it now — so
// every POA already sitting with a client has no stored link and would get a "go find
// the original SafeSend email" nudge instead. On a POA sent four months ago that is a
// poor nudge, so the button stays hidden until we know whether SafeSend returns a
// usable link (watch the next real send in the Function logs) or exposes a native
// resend endpoint.
//
// The /api/poa-remind-client endpoint deploys either way and can be exercised by hand.
// Flip this to true to surface the button; nothing else needs to change.
//
// The sent-on date and aging are NOT gated by this — that half works today.
export const REMINDERS_ENABLED = false;

// Form 2848's Line 3 table has exactly three rows. More matters than that cannot be
// rendered onto the form — they need a second Form 2848 rather than silent truncation.
export const MAX_TAX_MATTERS = 3;

// IRS rule: future years may not exceed 3 years beyond Dec 31 of the current year
export const MAX_FUTURE_YEAR = new Date().getFullYear() + 3;
export const MIN_YEAR = 1990;
